import styles from "./Post.module.scss";
import {useParams} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import ApiInterface from "@/commons/api/ApiInterface";
import Cookies from "js-cookie";
import {environment} from "@/commons/configs/Config";
import {useInfoBox} from "@/commons/infoBox/InfoBox";
import Redirector from "@/commons/utils/Redirects";
import {isStaffer} from "@/commons/api/apiPages/Details";
import {checkText, getResponseText} from "@/commons/tickets/TicketsCommons";
import Header from "@/commons/pages/borders/header/Header";
import Footer from "@/commons/pages/borders/footer/Footer";
import Comment from "./objects/Comment";
import reportUpdateSound from "@/commons/resources/sounds/reportUpdateSound.mp3";
import reportChangedSound from "@/commons/resources/sounds/reportChangedSound.mp3";
import {connectWebSocket, disconnectWebSocket} from "@/commons/websocket/websocketClient";
import Editor from "@/reports/editor/Editor";
import CloudImage from "@/commons/components/image/CloudImage";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import Categories from "@/commons/objects/category/Categories";
import {useUser} from "@/contexts/UserContext";
import CustomSelect from "@/commons/components/fields/customSelect/CustomSelect";

// Tipi
export interface StaffGroup {
    id: number;
    groupName: string;
    iconUUID: string;
}

export interface UserData {
    id: string;
    username: string;
    staffGroup: StaffGroup;
}

export interface Category {
    id: number;
    name: string;
    orderId?: number;
    usable?: string;
}

export interface PostData {
    id: number;
    title: string;
    content: string;
    status: string;
    time: string;
    categoryId: Category;
    userId: UserData;
}

export interface CommentData {
    id: string;
    content: string;
    time: string;
    userId: {
        id: string;
        username: string;
        staffGroup: {
            iconUUID: string;
            groupName: string;
            id: number;
        };
    };
}

export interface WebSocketUpdate {
    status?: "OPENED" | "CLOSED";
    comment?: CommentData;
    category?: Category;
}

const SinglePost = () => {
    const {isStaff, userLoadingComplete} = useUser();
    const {id} = useParams<{ id: string }>();
    const {addInfo, addPersistentInfo, updatePersistentInfo} = useInfoBox();

    const [postJSON, setPostJSON] = useState<PostData | null>(null);
    const [commentComponents, setCommentComponents] = useState<JSX.Element[]>([]);
    const [liveStatus, setLiveStatus] = useState<string>("");
    const [liveCategory, setLiveCategory] = useState<string>("");
    const [value, setValue] = useState<string>("");
    const [page, setPage] = useState<number>(-1);
    const [maxPage, setMaxPage] = useState<number>(1);
    const [parsedBody, setParsedBody] = useState<React.ReactNode[]>([]);
    const [authorInfo, setAuthorInfo] = useState<React.ReactNode>(null);

    const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

    const pageRef = useRef<number>(0);
    const maxPageRef = useRef<number>(1);
    const categoryRef = useRef<number>(0);

    const itemsPerPage = 20;

    const appendComment = (commentJson: CommentData) => {
        const key = `comment_${commentJson.id}`;
        if (commentComponents.some((c) => c.key === key)) return;
        setCommentComponents((prev) => [
            ...prev,
            <Comment key={key} commentJson={commentJson} onClickUsername={redirectToProfile}/>,
        ]);
    };

    const fetchTotalComments = async (): Promise<number> => {
        const token = Cookies.get(environment.accessToken);
        if (!token) return 0;

        const res = await fetch(`${environment.apiUrlOne}/comment/getComments/${id}`, {
            method: "POST",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({page: 0, items: 1}),
        });

        if (res.status !== 200) return 0;
        const json = await res.json();
        return json.totalElements || 0;
    };

    const loadComments = async () => {
        setCommentComponents([]);
        const token = Cookies.get(environment.accessToken);
        if (!token) return;

        const res = await fetch(`${environment.apiUrlOne}/comment/getComments/${id}`, {
            method: "POST",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({page, items: itemsPerPage}),
        });

        if (res.status !== 200) return;
        const json = await res.json();
        (json.content as CommentData[]).forEach(appendComment);
    };

    const redirectToProfile = async (userId: string, e: React.MouseEvent) => {
        if (await isStaffer()) {
            Redirector.domainSendClick("admin", `user/${userId}`, e as any);
        }
    };


    const loadPostJson = async () => {
        const token = Cookies.get(environment.accessToken);
        const res = await fetch(`${environment.apiUrlOne}/posts/getPost/${id}`, {
            method: "GET",
            headers: {Authorization: String(token)},
        });

        if (res.status !== 200) {
            Redirector.send("/", null, {
                popupData: {type: "alert", title: "Errore", text: "Post non trovato"},
            });
            return;
        }

        const json = await res.json();
        setPostJSON(json);
    };

    const loadAll = async () => {
        if (!postJSON) return;
        const {title, status, categoryId, userId, content, time} = postJSON;

        categoryRef.current = categoryId.id;
        setSelectedCategoryId(categoryId.id);
        if(status.toLowerCase() === "open"){
            setLiveStatus("opened")
        }else{
            setLiveStatus("closed")
        }
        setLiveCategory(categoryId.name);

        const date = new Date(time).toLocaleDateString();

        setAuthorInfo(
            <span className="clickable"
                  onClick={(e) => redirectToProfile(String(userId.id), e as unknown as React.MouseEvent)}>
                Autore: {userId.username}{" "}<CloudImage uuid={userId.staffGroup.iconUUID}
                                                          alt={userId.staffGroup.groupName}/>
            </span>
        );

        document.getElementById("postTitle")!.innerText = title;
        document.getElementById("postDate")!.innerText = date;

        setParsedBody([
            <Comment
                key="postBody"
                commentJson={{
                    id: "postBody",
                    content,
                    time,
                    userId
                }}
                asComment={false}
            />
        ]);
    };

    const updateButtons = async () => {
        const btn = document.getElementById("responseButton") as HTMLButtonElement;
        const closeBtn = document.getElementById("changeReportStatus") as HTMLButtonElement;
        if (!btn || !closeBtn) return;

        const text = (await checkText(value)) || "";
        btn.disabled = text === "empty" || liveStatus !== "opened";
        btn.innerText = "Rispondi";
        btn.onclick = addResponse;

        if (text === "empty") {
            closeBtn.innerText = liveStatus === "opened" ? "Chiudi Report" : "Riapri Report";
            closeBtn.onclick = liveStatus === "opened" ? closeReport : openReport;
        } else {
            closeBtn.innerText = liveStatus === "opened" ? "Rispondi e chiudi" : "Riapri rispondendo";
            closeBtn.onclick = async () => {
                await addResponse();
                if (liveStatus === "opened") closeReport();
            };
        }
    };

    const playSound = (src: string) => {
        const audio = new Audio(src);
        audio.play().catch(() => {
        });
    };

    const addResponse = async () => {
        const btn = document.getElementById("responseButton") as HTMLButtonElement;
        btn.disabled = true;
        btn.innerText = "Pubblico la risposta...";
        btn.classList.add("button--loading");

        const text = await getResponseText(value, {addInfo, addPersistentInfo, updatePersistentInfo});
        if (!text || text === "empty") {
            updatePersistentInfo({
                type: "alert",
                title: "Errore",
                text: "Il commento non può essere vuoto!",
                destroyIn: 6,
            });
            btn.classList.remove("button--loading");
            btn.disabled = false;
            btn.innerText = "Errore";
            return;
        }

        if (liveStatus !== "opened") {
            const opened = await ApiInterface.openReport(id!, "<span style='color: #52ea00'>Riapertura automatica</span>");
            if (!opened) return;
        }

        const res = await ApiInterface.addComment(text, id!);
        if (res.status === 200) {
            setValue("");
            updatePersistentInfo({
                type: "success",
                title: "Risposta inviata",
                text: "Risposta inviata correttamente",
                destroyIn: 3,
            });
        } else {
            updatePersistentInfo({
                type: "alert",
                title: "Errore",
                text: "Errore durante l'invio",
                destroyIn: 6,
            });
        }

        btn.classList.remove("button--loading");
        btn.disabled = false;
        btn.innerText = "Rispondi";
    };

    const closeReport = async () => {
        const res = await ApiInterface.closeReport(id!, "<span style='color: #b70000'>Report chiuso</span>");
        if (res.status !== 200) {
            addInfo("alert", "Errore", "Errore durante la chiusura del report.");
        }
    };

    const openReport = async () => {
        const res = await ApiInterface.openReport(id!, "<span style='color: #52ea00'>Report riaperto</span>");
        if (res.status !== 200) {
            addInfo("alert", "Errore", "Errore durante la riapertura del report.");
        }
    };

    useEffect(() => {
        if(!userLoadingComplete) return
            loadPostJson();
            setPage(0)
    }, [userLoadingComplete]);

    useEffect(() => {
        if (postJSON) {
            loadAll();
            loadCategories()
        }
    }, [postJSON]);

    const handleCategoryChange = async (categoryId:string) => {
        const newCategoryId = parseInt(categoryId);
        const response = await ApiInterface.editCategory(
            id!,
            newCategoryId,
            "Categoria aggiornata<br/> %old% -> %new%"
        );

        if (response.status === 200) {
            setSelectedCategoryId(newCategoryId);
            const changed = categoryOptions.find(c => c.id === newCategoryId)?.name;
            const current = categoryOptions.find(c => c.id === categoryRef.current)?.name;
            addInfo("success", "categoria cambiata", `la categoria è stata cambiata da ${current} a ${changed}.`, 5);
            categoryRef.current = newCategoryId;
        } else {
            addInfo("alert", "Errore", "C'è stato un errore durante il cambio della categoria.");
        }
    };


    const loadCategories = async () => {
        if(!isStaff) return
        const res = await ApiInterface.getCategories();
        if (res.status !== 200) return;

        const json = await res.json();
        const allCats = new Categories();
        allCats.addAllCat(json);

        const usableCategories = allCats.categories.filter((cat: { orderId: any; }) => (cat.orderId ?? 0) > -1);
        setCategoryOptions(usableCategories);
    };

    useEffect(() => {
        updateButtons();
    }, [value, liveStatus]);

    useEffect(() => {
        if(page === -1) return;
        pageRef.current = page;
        loadComments();
        fetchTotalComments().then((total) => {
            const p = Math.max(1, Math.ceil(total / itemsPerPage));
            setMaxPage(p);
            maxPageRef.current = p;
        });
    }, [page]);

    useEffect(() => {
        if (!id) return;
        const handle = (data: WebSocketUpdate) => {
            const realPage = pageRef.current + 1;
            const realMaxPage = maxPageRef.current;
            if (data.status) {
                setLiveStatus(data.status.toLowerCase());
                playSound(reportChangedSound);
            } else if (data.comment) {
                playSound(reportUpdateSound);
                if (realPage === realMaxPage) appendComment(data.comment);
            } else if (data.category) {
                setLiveCategory(data.category.name);
                playSound(reportUpdateSound);
            }
        };
        connectWebSocket(id, handle);
        return () => disconnectWebSocket();
    }, []);

    if (!postJSON) return null;

    const categoryOption = () => {
        if (!isStaff || !selectedCategoryId || categoryOptions.length === 0) return null;

        return (
            <>
                {categoryOptions.length > 0 && (
                    <div className={styles.categoryAdminBox}>
                        <CustomSelect
                            label={"Modifica categoria"}
                            options={categoryOptions.map((cat) => ({
                                label: cat.name,
                                value: cat.id,
                                disabled: cat.usable?.toLowerCase() === "separator",
                            }))}
                            onChange={(categoryValue) => handleCategoryChange(categoryValue)}
                            defaultValue={selectedCategoryId ?? ""}
                            value={selectedCategoryId ?? ""}
                            visibleOptionsCount={10}
                        />
                    </div>
                )}
            </>
        );
    };


    const pagination = () => {
        const renderLeftControls = () => {
            if (page <= 0) return null;
            return (
                <>
                    {page > 1 && (
                        <CustomButton onClick={() => setPage(0)}>&lt;&lt;</CustomButton>
                    )}
                    <CustomButton onClick={() => setPage(page - 1)}>&lt;</CustomButton>
                </>
            );
        };


        const renderPageNumbers = () => {
            return [...Array(7)].map((_, i) => {
                const newPage = page - 3 + i;
                if (newPage >= 0 && newPage < maxPage) {
                    const active = newPage === page;
                    return (
                        <CustomButton
                            className={`${active ? styles.activePage : ""}`}
                            children={newPage + 1}
                            onClick={!active ? () => setPage(newPage) : undefined}
                        />
                    );
                }
                return null;
            });
        };

        const renderRightControls = () => {
            if (page >= maxPage - 1) return null;

            return (
                <>
                    <CustomButton onClick={() => setPage(page + 1)}>&gt;</CustomButton>
                    {page < maxPage - 2 && (
                        <CustomButton onClick={() => setPage(maxPage - 1)}>&gt;&gt;</CustomButton>
                    )}
                </>
            );
        };


        return (
            <div className={styles.pageSelection}>
                {renderLeftControls()}
                {renderPageNumbers()}
                {renderRightControls()}
            </div>
        );
    }

    return (
        <>
            <meta name="description" content={`EBLCraft report #${id}`}/>
            <div className={"page__body"}>
                <Header/>
                <div className={"page__content"}>
                    <div className={styles.Post_ReportsMainBox}>
                        <div className={styles.PostInfo}>
                            <div id="postTitle" className={styles.postTitle}></div>
                            <div id="postDate" className={styles.postDate}></div>
                            <div id="postStatus" className={styles.postStatus}>
                                {liveStatus === "opened" ? (
                                    <span style={{color: "green"}}>Aperto</span>
                                ) : (
                                    <span style={{color: "red"}}>Chiuso</span>
                                )}
                            </div>
                            <div id="postCategory" className={styles.postCategory}>
                                {liveCategory && `Categoria: ${liveCategory}`}
                            </div>
                            <div className={styles.postAuthor}>{authorInfo}</div>
                            <div id="postBody" className={styles.postBody}>{parsedBody}</div>
                        </div>

                        <div className={styles.PostComments}>{commentComponents}</div>

                        <b>Rispondi:</b>
                        <Editor value={value} onChange={setValue}/>

                        <div className={styles.buttonsSection}>
                            {categoryOption()}
                            <div className={styles.post_buttons}>
                                <CustomButton id={"responseButton"} children={"Rispondi"}/>
                                <CustomButton id={"changeReportStatus"} children={undefined}/>
                            </div>
                            {pagination()}
                        </div>
                    </div>
                </div>
                <Footer/>
            </div>
        </>
    );
};

export default SinglePost;
